import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { ToastContainer } from 'react-toastify';

import TeamFunctionManager from './TeamFunctionManager';
import TeamMemberCard from './TeamMemberCard';
import TeamMemberModal from './TeamMemberModal';

const TeamManager = ({
  venueOwnerships,
  teamMemberships,
  pendingInvitations,
  addingNewInvitation,
  addTeamMemberClicked,
  newInvitation,
  cancelNewInvitation,
  newInvitationFirstNameChanged,
  newInvitationLastNameChanged,
  newInvitationEmailChanged,
  roles,
  newInvitationRoleIdChanged,
  newInvitationVenueOwnershipClicked,
  csrfToken,
  createInvitation,
  team,
  newInvitationEnableAllVenues,
  newInvitationSubmitting,
  newInvitationEnableAllVenuesClicked,
  newInvitationDisableAllVenuesClicked,
  teamManageableToEdit,
  editTeamManageable,
  cancelEditTeamManageable,
  deleteTeamManageable,
  teamManageableToEditUpdateFirstName,
  teamManageableToEditUpdateLastName,
  teamManageableToEditUpdateEmail,
  teamManageableToEditUpdateRoleId,
  teamManageableToEditVenueOwnershipClicked,
  teamManageableToEditEnableAllVenues,
  teamManageableToEditEnableAllVenuesClicked,
  teamManageableToEditDisableAllVenuesClicked,
  submitUpdateTeamManageable,
  resetPasswordForEmail,
  newInvitationCompanyChanged,
  teamManageableToEditCompanyChanged,
  // New props for redesign
  searchTerm,
  roleFilter,
  venueFilter,
  currentPage,
  pageSize,
  sortBy,
  sortDirection,
  selectedMemberIds,
  searchTermChanged,
  roleFilterChanged,
  venueFilterChanged,
  currentPageChanged,
  toggleMemberSelection,
  selectAllMembers,
  deselectAllMembers,
  clearFilters,
  sortChanged,
  teamFunctions,
  teamFunctionFilter,
  teamFunctionFilterChanged,
  newInvitationTeamFunctionIdsChanged,
  teamManageableToEditTeamFunctionIdsChanged,
  createTeamFunction,
  updateTeamFunction,
  deleteTeamFunction
}) => {
  const isEditing = Boolean(teamManageableToEdit && teamManageableToEdit.id);

  const editFormData = useMemo(() => {
    if (!teamManageableToEdit || !teamManageableToEdit.id) return null;
    return {
      id: teamManageableToEdit.id,
      type_for_ui: teamManageableToEdit.type_for_ui,
      firstName: teamManageableToEdit.first_name,
      lastName: teamManageableToEdit.last_name,
      email: teamManageableToEdit.email,
      roleId: teamManageableToEdit.role_id,
      company: teamManageableToEdit.company,
      venueOwnershipIds: teamManageableToEdit.venue_ownership_ids || [],
      teamFunctionIds: teamManageableToEdit.team_function_ids || [],
      errors: teamManageableToEdit.errors
    };
  }, [teamManageableToEdit]);

  const handleEditSubmit = (csrf, venues, formData, teamObj) => {
    const snakeCaseData = {
      ...teamManageableToEdit,
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      role_id: formData.roleId,
      company: formData.company,
      venue_ownership_ids: formData.venueOwnershipIds,
      team_function_ids: formData.teamFunctionIds || []
    };
    submitUpdateTeamManageable(csrf, teamObj, snakeCaseData);
  };

  const modalOpen = addingNewInvitation || isEditing;
  const modalMode = isEditing ? 'edit' : 'add';

  const handleModalClose = () => {
    if (isEditing) {
      cancelEditTeamManageable(teamManageableToEdit);
    } else {
      cancelNewInvitation();
    }
  };

  const handleEdit = (teamManageable) => {
    editTeamManageable(teamManageable, venueOwnerships);
  };

  return (
    <div className="row">
      <ToastContainer icon={false} />
      <div className="col-12">
        <TeamMemberCard
          teamMemberships={teamMemberships}
          pendingInvitations={pendingInvitations}
          roles={roles}
          venueOwnerships={venueOwnerships}
          team={team}
          csrfToken={csrfToken}
          searchTerm={searchTerm}
          roleFilter={roleFilter}
          venueFilter={venueFilter}
          teamFunctions={teamFunctions}
          teamFunctionFilter={teamFunctionFilter}
          currentPage={currentPage}
          pageSize={pageSize}
          sortBy={sortBy}
          sortDirection={sortDirection}
          selectedMemberIds={selectedMemberIds}
          onSearchChange={searchTermChanged}
          onRoleFilterChange={roleFilterChanged}
          onVenueFilterChange={venueFilterChanged}
          onTeamFunctionFilterChange={teamFunctionFilterChanged}
          onClearFilters={clearFilters}
          onPageChange={currentPageChanged}
          onToggleSelect={toggleMemberSelection}
          onSelectAll={selectAllMembers}
          onDeselectAll={deselectAllMembers}
          onSortChange={sortChanged}
          onAddMember={addTeamMemberClicked}
          onEdit={handleEdit}
          onDelete={deleteTeamManageable}
          onResetPassword={resetPasswordForEmail}
        />

        <div style={{ marginTop: 'var(--spacing-3xl, 24px)', marginBottom: 'var(--spacing-3xl, 24px)' }}>
          <TeamFunctionManager
            teamFunctions={teamFunctions}
            team={team}
            csrfToken={csrfToken}
            onCreateTeamFunction={createTeamFunction}
            onUpdateTeamFunction={updateTeamFunction}
            onDeleteTeamFunction={deleteTeamFunction}
          />
        </div>

        <TeamMemberModal
          isOpen={modalOpen}
          onClose={handleModalClose}
          mode={modalMode}
          formData={isEditing ? editFormData : newInvitation}
          roles={roles}
          venueOwnerships={venueOwnerships}
          team={team}
          csrfToken={csrfToken}
          enableAllVenues={isEditing ? teamManageableToEditEnableAllVenues : newInvitationEnableAllVenues}
          isSubmitting={newInvitationSubmitting}
          onFirstNameChange={isEditing ? teamManageableToEditUpdateFirstName : newInvitationFirstNameChanged}
          onLastNameChange={isEditing ? teamManageableToEditUpdateLastName : newInvitationLastNameChanged}
          onEmailChange={isEditing ? teamManageableToEditUpdateEmail : newInvitationEmailChanged}
          onRoleIdChange={isEditing ? teamManageableToEditUpdateRoleId : newInvitationRoleIdChanged}
          onCompanyChange={isEditing ? teamManageableToEditCompanyChanged : newInvitationCompanyChanged}
          onVenueOwnershipClick={isEditing ? teamManageableToEditVenueOwnershipClicked : newInvitationVenueOwnershipClicked}
          onEnableAllVenuesClick={isEditing ? teamManageableToEditEnableAllVenuesClicked : newInvitationEnableAllVenuesClicked}
          onDisableAllVenuesClick={isEditing ? teamManageableToEditDisableAllVenuesClicked : newInvitationDisableAllVenuesClicked}
          onSubmit={isEditing ? handleEditSubmit : createInvitation}
          teamFunctions={teamFunctions}
          onTeamFunctionIdsChange={isEditing ? teamManageableToEditTeamFunctionIdsChanged : newInvitationTeamFunctionIdsChanged}
          onCreateTeamFunction={createTeamFunction}
        />
      </div>
    </div>
  );
};

TeamManager.propTypes = {
  venueOwnerships: PropTypes.array.isRequired,
  teamMemberships: PropTypes.array,
  pendingInvitations: PropTypes.array,
  addingNewInvitation: PropTypes.bool,
  addTeamMemberClicked: PropTypes.func.isRequired,
  newInvitation: PropTypes.object,
  cancelNewInvitation: PropTypes.func.isRequired,
  newInvitationFirstNameChanged: PropTypes.func.isRequired,
  newInvitationLastNameChanged: PropTypes.func.isRequired,
  newInvitationEmailChanged: PropTypes.func.isRequired,
  roles: PropTypes.array.isRequired,
  newInvitationRoleIdChanged: PropTypes.func.isRequired,
  newInvitationVenueOwnershipClicked: PropTypes.func.isRequired,
  csrfToken: PropTypes.string.isRequired,
  createInvitation: PropTypes.func.isRequired,
  team: PropTypes.object.isRequired,
  newInvitationEnableAllVenues: PropTypes.bool,
  newInvitationSubmitting: PropTypes.bool,
  newInvitationEnableAllVenuesClicked: PropTypes.func.isRequired,
  newInvitationDisableAllVenuesClicked: PropTypes.func.isRequired,
  teamManageableToEdit: PropTypes.object,
  editTeamManageable: PropTypes.func.isRequired,
  cancelEditTeamManageable: PropTypes.func.isRequired,
  deleteTeamManageable: PropTypes.func.isRequired,
  teamManageableToEditUpdateFirstName: PropTypes.func.isRequired,
  teamManageableToEditUpdateLastName: PropTypes.func.isRequired,
  teamManageableToEditUpdateEmail: PropTypes.func.isRequired,
  teamManageableToEditUpdateRoleId: PropTypes.func.isRequired,
  teamManageableToEditVenueOwnershipClicked: PropTypes.func.isRequired,
  teamManageableToEditEnableAllVenues: PropTypes.bool,
  teamManageableToEditEnableAllVenuesClicked: PropTypes.func.isRequired,
  teamManageableToEditDisableAllVenuesClicked: PropTypes.func.isRequired,
  submitUpdateTeamManageable: PropTypes.func.isRequired,
  resetPasswordForEmail: PropTypes.func.isRequired,
  newInvitationCompanyChanged: PropTypes.func.isRequired,
  teamManageableToEditCompanyChanged: PropTypes.func.isRequired,
  // New props for redesign
  searchTerm: PropTypes.string,
  roleFilter: PropTypes.array,
  venueFilter: PropTypes.array,
  currentPage: PropTypes.number,
  pageSize: PropTypes.number,
  sortBy: PropTypes.string,
  sortDirection: PropTypes.string,
  selectedMemberIds: PropTypes.array,
  searchTermChanged: PropTypes.func.isRequired,
  roleFilterChanged: PropTypes.func.isRequired,
  venueFilterChanged: PropTypes.func.isRequired,
  currentPageChanged: PropTypes.func.isRequired,
  toggleMemberSelection: PropTypes.func.isRequired,
  selectAllMembers: PropTypes.func.isRequired,
  deselectAllMembers: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
  sortChanged: PropTypes.func.isRequired,
  teamFunctions: PropTypes.array.isRequired,
  newInvitationTeamFunctionIdsChanged: PropTypes.func.isRequired,
  teamManageableToEditTeamFunctionIdsChanged: PropTypes.func.isRequired,
  createTeamFunction: PropTypes.func.isRequired,
  updateTeamFunction: PropTypes.func.isRequired,
  deleteTeamFunction: PropTypes.func.isRequired
};

TeamManager.defaultProps = {
  teamMemberships: [],
  pendingInvitations: [],
  addingNewInvitation: false,
  searchTerm: '',
  roleFilter: [],
  venueFilter: [],
  currentPage: 1,
  pageSize: 10,
  sortBy: 'name',
  sortDirection: 'asc',
  selectedMemberIds: []
};

export default TeamManager;
